import { searchAssetsTypes } from '@atlassian/search-client/assets';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { assetsObjectTypesQuery } from '@atlassian/search-client/object-types';

import type { SelectFilterState } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys1P } from '../../../../../common/constants/product-keys';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

const productKeys1P: ProductKeys1P[] = [ProductKeys.Assets];

export const filter: Readonly<{
	id: 'assetsType';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'assetsType'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: import('../../../../../common/constants/filters/cloud-config/types').CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'assetsType'>;
	getInitialState: (
		config: import('../../../../../common/constants/filters/cloud-config/types').CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'assetsType'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'assetsType',
	products: productKeys1P,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'assetsType',
		fetcher: async (queryParams, config) => {
			const headers = buildAtlAttributionHeaders({
				cloudId: config.cloudId,
				activationId: config.activationId,
				product: config.primaryProduct,
			});

			const allTypes = await assetsObjectTypesQuery(queryParams, headers);
			const hydratedTypeOptions = allTypes.data?.assets_objectTypesByIds?.map((type) => {
				return {
					trackingKey: type.id,
					value: type.id,
					queryParamValue: type.id,
					label: type.name || '',
					product: config.product,
					isSuggested: false,
					isSelected: false,
					avatarUrl: type.icon.url16,
					metadata: {
						schemaName: type.schema.name,
					},
				};
			});

			return hydratedTypeOptions ?? [];
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			const suggestedTypes = await searchAssetsTypes();
			const recommendedTypeOptions = suggestedTypes.map((type) => {
				return {
					trackingKey: type.ari,
					value: type.ari,
					queryParamValue: type.ari,
					label: type.name || '',
					product: config.product,
					isSuggested: true,
					isSelected: false,
					avatarUrl: type.icon.url16,
					metadata: {
						schemaName: type.schemaLabel,
					},
				};
			});

			return recommendedTypeOptions;
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			const resultTypes = await searchAssetsTypes(query);
			const searchTypeOptions = resultTypes.map((type) => {
				return {
					trackingKey: type.ari,
					value: type.ari,
					queryParamValue: type.ari,
					label: type.name || '',
					product: config.product,
					isSuggested: false,
					isSelected: false,
					avatarUrl: type.icon.url16,
					metadata: {
						schemaName: type.schemaLabel,
					},
				};
			});
			return searchTypeOptions;
		},
	},
	isSuggestedFilter: (primaryProduct) => primaryProduct === ProductKeys.Assets,
});
